import { useEffect, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiService } from '@/services/api';
import type { Filial } from '@/types';
import toast from 'react-hot-toast';

async function fetchFiliais(): Promise<Filial[]> {
  // garanta que o service retorna esse tipo; se necess�rio ajuste aqui
  const data = await apiService.listarFiliais();
  return data as Filial[];
}

export const useFiliais = (options?: { enabled?: boolean }) => {
  const enabled = options?.enabled ?? true;
  const queryClient = useQueryClient();

  // Query para listar filiais
  const {
    data: filiais = [],
    isLoading,
    isError,
    error,
    refetch,
  } = useQuery<Filial[], Error>({
    queryKey: ['filiais'],
    queryFn: fetchFiliais,
    // V5: cacheTime -> gcTime
    staleTime: 0,
    gcTime: 0,
    refetchOnMount: 'always',
    refetchOnWindowFocus: true,
    retry: 2,
    enabled,
  });

  // Exibe toast em caso de erro da query (callbacks foram removidos no v5)
  useEffect(() => {
    if (isError) {
      console.error('Erro ao carregar filiais:', error);
      toast.error('Erro ao carregar lista de filiais');
    }
  }, [isError, error]);

  // Loga retorno vazio para facilitar diagnóstico
  useEffect(() => {
    try {
      if (!isLoading) {
        console.log(`[FRONT][FILIAIS] Carregadas: ${Array.isArray(filiais) ? filiais.length : 0}`);
      }
    } catch {}
  }, [isLoading, filiais]);

  // Mutation para testar conex�o
  const testarConexaoMutation = useMutation<boolean, unknown, string>({
    mutationKey: ['testarConexao'],
    mutationFn: async (filialId: string) => {
      const result = await apiService.testarConexaoFilial(filialId);
      return result as boolean;
    },
    onSuccess: (conectado, filialId) => {
      if (conectado) {
        toast.success(`Conex�o com ${filialId} estabelecida com sucesso`);
      } else {
        toast.error(`Falha na conex�o com ${filialId}`);
      }
      // Atualiza o cache das filiais
      queryClient.invalidateQueries({ queryKey: ['filiais'] });
    },
    onError: (err, filialId) => {
      console.error(`Erro ao testar conex�o da filial ${filialId}:`, err);
      toast.error(`Erro ao testar conex�o com ${filialId}`);
    },
  });

  // Fun��o para testar conex�o de uma filial
  const testarConexao = useCallback(
    (filialId: string) => {
      testarConexaoMutation.mutate(filialId);
    },
    [testarConexaoMutation]
  );

  // Fun��o para recarregar filiais
  const recarregarFiliais = useCallback(() => {
    refetch();
  }, [refetch]);

  // Fun��o para obter filial por ID
  const obterFilialPorId = useCallback(
    (filialId: string): Filial | undefined => filiais.find((f) => f.id === filialId),
    [filiais]
  );

  // Fun��o para filtrar filiais por status
  const obterFiliaisPorStatus = useCallback(
    (status: Filial['status']): Filial[] => filiais.filter((f) => f.status === status),
    [filiais]
  );

  return {
    filiais,
    isLoading,
    isError,
    error,
    testarConexao,
    recarregarFiliais,
    obterFilialPorId,
    obterFiliaisPorStatus,
    // v5: isLoading -> isPending
    isTestingConnection: testarConexaoMutation.isPending,
  };
};
